import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeAllowBr from './rehypeAllowBr';
import {
  GuideTemplateRoot,
  MARKDOWN_CLASS,
  TABLE_FLUID_CLASS,
  TABLE_SCROLL_CLASS,
} from './styles';
import wrapGuideTables from './wrapGuideTables';

type GuideTemplateProps = {
  /** 어드민 관리 가이드 원문. HTML(레거시) 과 마크다운(어드민 전환 후) 이 공존 */
  content: string;
  className?: string;
};

/**
 * 판별 규칙(티켓 지정): 앞쪽 공백·개행·BOM 을 걷어낸 첫 글자가 꺾쇠면 HTML, 그 외 마크다운.
 * trimStart 는 U+FEFF(BOM) 도 공백으로 제거.
 */
const isHtmlGuide = (content: string) => content.trimStart().startsWith('<');

const markdownComponents = {
  a: ({
    node: _node,
    ...props
  }: React.ComponentProps<'a'> & { node?: unknown }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
  table: ({
    node: _node,
    ...props
  }: React.ComponentProps<'table'> & { node?: unknown }) => (
    <div className={TABLE_SCROLL_CLASS}>
      <table {...props} className={TABLE_FLUID_CLASS} />
    </div>
  ),
};

const GuideTemplate = ({ content, className }: GuideTemplateProps) => {
  const isHtml = isHtmlGuide(content);
  const html = useMemo(
    () => (isHtml ? wrapGuideTables(content) : ''),
    [isHtml, content],
  );

  if (!content) return null;

  if (isHtml) {
    /* 신뢰된 어드민 HTML. 인라인 onclick 탭 전환이 sanitize 시 깨져 그대로 주입 — XSS 리스크 수용 */
    return (
      <GuideTemplateRoot
        className={className}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <GuideTemplateRoot className={className}>
      <div className={MARKDOWN_CLASS}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeAllowBr]}
          components={markdownComponents}
        >
          {content}
        </ReactMarkdown>
      </div>
    </GuideTemplateRoot>
  );
};

export default GuideTemplate;
